import React from 'react';
import { Helmet } from 'react-helmet-async';
import CANONICAL_ORIGIN from '../utils/canonicalOrigin';
import { portalForHostname, productLabelForHostname } from '../utils/portalExperience';
import { PRESETS } from '../brandKits';

/**
 * Drop-in <SeoHead /> for marketing / public pages.
 *
 * Why this exists:
 *   Most secondary pages (about, contact, signup, login, etc.) had no
 *   per-page meta at all, so the pre-rendered HTML for every route was
 *   indistinguishable from `/` (same title, same description, no canonical).
 *   Google deduplicates that and only indexes one copy. This component
 *   gives every page the unique title + description + canonical it needs
 *   without each page reimplementing the same Helmet boilerplate.
 *
 * Always pass a leading-slash `path` (e.g. "/about"). The component
 * resolves it against the canonical production origin so og:url and
 * canonical never accidentally point at the prerender loopback host.
 */
const DEFAULT_OG_IMAGE = '/logo-white.png';

export default function SeoHead({
    title,
    description,
    path = '/',
    image = DEFAULT_OG_IMAGE,
    type = 'website',
    noindex = false,
    children = null,
}) {
    const portal = portalForHostname();
    const displayTitle = productLabelForHostname(title);
    const displayDescription = productLabelForHostname(description);
    const displayImage = portal && (!image || image === DEFAULT_OG_IMAGE)
        ? PRESETS[portal.kitId].logos.onLight : image;
    const url = `${CANONICAL_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
    const imgUrl = displayImage && displayImage.startsWith('http') ? displayImage : `${CANONICAL_ORIGIN}${displayImage || DEFAULT_OG_IMAGE}`;
    return (
        <Helmet>
            <title>{displayTitle}</title>
            <meta name="description" content={displayDescription} />
            <link rel="canonical" href={url} />
            {(noindex || portal) && <meta name="robots" content="noindex, nofollow" />}
            <meta property="og:title" content={displayTitle} />
            <meta property="og:description" content={displayDescription} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={imgUrl} />
            <meta property="og:type" content={type} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={displayTitle} />
            <meta name="twitter:description" content={displayDescription} />
            <meta name="twitter:image" content={imgUrl} />
            {children}
        </Helmet>
    );
}
